import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  List,
  ListItem,
  ListItemText,
  Stack,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Paper,
  ListSubheader,
  Chip,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import {
  CaseEditorStepFrame,
  caseManagerInsetSurfaceSx,
  caseManagerPalette,
  caseManagerSelectSx,
  publicBodyFontFamily,
  publicTextFieldSx,
} from '../caseManagerStyles';

const PatientNarrativeComposer = ({ data, onUpdate, validationErrors = [] }) => {
  const narrative = data.patientNarrative || '';
  const examinationFindings = data.examinationFindings || '';
  const charCount = narrative.length;
  const findingsCharCount = examinationFindings.length;
  const hasError = validationErrors.some(error =>
    error.toLowerCase().includes('patient narrative')
  );

  const accentGroups = [
    {
      label: 'English accents',
      options: [
        'Standard English (Received Pronunciation)',
        'London/Cockney accent',
        'Northern English (Yorkshire/Lancashire)',
        'Midlands English (Birmingham/Black Country)',
        'West Country English (Devon/Cornwall)',
        'Geordie (Newcastle)',
        'Scouse (Liverpool)',
        'Manchester accent'
      ]
    },
    {
      label: 'Scottish accents',
      options: [
        'Standard Scottish accent',
        'Glasgow accent',
        'Edinburgh accent',
        'Highland Scottish accent',
        'Lowland Scottish accent'
      ]
    },
    {
      label: 'Irish accents',
      options: [
        'Standard Irish accent',
        'Dublin accent',
        'Cork accent',
        'Belfast (Northern Irish) accent',
        'Rural Irish accent'
      ]
    },
    {
      label: 'Welsh accents',
      options: [
        'Welsh accent',
        'Cardiff accent',
        'North Wales accent'
      ]
    }
  ];

  const handleAccentChange = (value) => {
    if (value === 'custom') {
      onUpdate({
        characterAccent: 'custom',
        characterAccentCustom: data.characterAccentCustom || ''
      });
    } else {
      onUpdate({
        characterAccent: value,
        characterAccentCustom: ''
      });
    }
  };

  const handleAccentCustomChange = (customText) => {
    onUpdate({ characterAccentCustom: customText });
  };

  const resolvePersonalityText = () => {
    const personalityMode = (data.characterPersonality || '').toString().trim().toLowerCase();
    if (personalityMode === 'custom') {
      return data.characterPersonalityCustom || '';
    }
    return data.characterPersonalityCustom || data.characterPersonality || '';
  };

  const handlePersonalityTextChange = (personalityText) => {
    const hasContent = Boolean(personalityText.trim());
    onUpdate({
      characterPersonality: hasContent ? 'custom' : '',
      characterPersonalityCustom: personalityText,
    });
  };

  return (
    <CaseEditorStepFrame>
      <Grid container spacing={1.8}>
        <Grid item xs={12} xl={7}>
          <Stack spacing={1.7}>
            <Paper sx={{ ...caseManagerInsetSurfaceSx, p: { xs: 1.6, md: 1.9 } }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ md: 'center' }}>
                <Box>
                  <Typography sx={{ color: caseManagerPalette.ink, fontWeight: 800, fontFamily: publicBodyFontFamily }}>
                    Patient story
                  </Typography>
                  <Typography sx={{ mt: 0.35, color: caseManagerPalette.inkSoft, fontSize: '0.92rem', lineHeight: 1.5, fontFamily: publicBodyFontFamily }}>
                    Focus on what the patient knows, what they volunteer naturally, and what only appears when the student asks the right question.
                  </Typography>
                </Box>
                <Chip label={`${charCount} characters`} sx={{ fontFamily: publicBodyFontFamily, fontWeight: 700 }} />
              </Stack>
            </Paper>

            <Alert
              icon={<LightbulbIcon fontSize="inherit" />}
              severity="info"
              sx={{
                borderRadius: '16px',
                bgcolor: caseManagerPalette.heroBlueMist,
                border: `1px solid ${caseManagerPalette.heroBlueSoft}`,
                '& .MuiAlert-message': {
                  width: '100%',
                },
              }}
            >
              <Typography sx={{ color: caseManagerPalette.ink, fontWeight: 800, mb: 0.9, fontFamily: publicBodyFontFamily }}>
                Useful writing prompts
              </Typography>
              <Grid container spacing={1.2}>
                <Grid item xs={12} sm={6}>
                  <List dense disablePadding>
                    <ListItem disableGutters sx={{ py: 0.3 }}>
                      <ListItemText primary="Open with what brought the patient in today." primaryTypographyProps={{ fontFamily: publicBodyFontFamily }} />
                    </ListItem>
                    <ListItem disableGutters sx={{ py: 0.3 }}>
                      <ListItemText primary="Build in timing, progression, and key red flags." primaryTypographyProps={{ fontFamily: publicBodyFontFamily }} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <List dense disablePadding>
                    <ListItem disableGutters sx={{ py: 0.3 }}>
                      <ListItemText primary="Include worries, expectations, or emotional cues." primaryTypographyProps={{ fontFamily: publicBodyFontFamily }} />
                    </ListItem>
                    <ListItem disableGutters sx={{ py: 0.3 }}>
                      <ListItemText primary="Leave some information hidden unless it is asked." primaryTypographyProps={{ fontFamily: publicBodyFontFamily }} />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </Alert>

            <Paper sx={{ ...caseManagerInsetSurfaceSx, p: { xs: 1.6, md: 1.9 } }}>
              <TextField
                fullWidth
                multiline
                minRows={10}
                maxRows={18}
                label="Patient Narrative"
                placeholder="Describe the patient’s presenting complaint, history, concerns, and the information they should reveal during the consultation."
                value={narrative}
                onChange={(event) => onUpdate({ patientNarrative: event.target.value })}
                required
                error={hasError}
                helperText={
                  hasError
                    ? 'A patient narrative is required to drive the AI conversation.'
                    : 'This is the core script that drives what the patient knows and how they answer.'
                }
                sx={{
                  ...publicTextFieldSx,
                  '& .MuiOutlinedInput-root': {
                    ...publicTextFieldSx['& .MuiOutlinedInput-root'],
                    alignItems: 'flex-start',
                    fontFamily: publicBodyFontFamily,
                    fontSize: '0.97rem',
                    lineHeight: 1.8,
                  },
                }}
              />

              <Divider sx={{ my: 1.7 }} />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 1.4 }}>
                <Box>
                  <Typography sx={{ color: caseManagerPalette.ink, fontWeight: 800, fontFamily: publicBodyFontFamily }}>
                    Optional examination findings
                  </Typography>
                  <Typography sx={{ mt: 0.3, color: caseManagerPalette.inkSoft, fontSize: '0.9rem', lineHeight: 1.45, fontFamily: publicBodyFontFamily }}>
                    Add free-text findings only if the case needs them for examiner questions, investigations, or later expansion.
                  </Typography>
                </Box>
                <Chip label={`${findingsCharCount} characters`} sx={{ fontFamily: publicBodyFontFamily, fontWeight: 700 }} />
              </Stack>

              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={8}
                label="Examination Findings"
                placeholder="Optional free-text examination findings, observations, or physical signs."
                value={examinationFindings}
                onChange={(event) => onUpdate({ examinationFindings: event.target.value })}
                helperText="Leave this blank if the case is purely history-focused."
                sx={publicTextFieldSx}
              />
            </Paper>
          </Stack>
        </Grid>

        <Grid item xs={12} xl={5}>
          <Stack spacing={1.7}>
            <Paper sx={{ ...caseManagerInsetSurfaceSx, p: { xs: 1.6, md: 1.9 } }}>
              <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.4 }}>
                <RecordVoiceOverIcon sx={{ color: caseManagerPalette.heroBlueMid }} />
                <Box>
                  <Typography sx={{ color: caseManagerPalette.ink, fontWeight: 800, fontFamily: publicBodyFontFamily }}>
                    Voice and manner
                  </Typography>
                  <Typography sx={{ mt: 0.3, color: caseManagerPalette.inkSoft, fontSize: '0.9rem', lineHeight: 1.45, fontFamily: publicBodyFontFamily }}>
                    These notes shape how the patient sounds and how they deliver information in conversation.
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={2}>
                <FormControl fullWidth sx={caseManagerSelectSx}>
                  <InputLabel>Accent or Speech Pattern</InputLabel>
                  <Select
                    label="Accent or Speech Pattern"
                    value={data.characterAccent || ''}
                    onChange={(e) => handleAccentChange(e.target.value)}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 400, borderRadius: '20px' } } }}
                  >
                    <MenuItem value="">
                      <em>Standard speech</em>
                    </MenuItem>

                    {accentGroups.map((group) => [
                      <ListSubheader
                        key={`header-${group.label}`}
                        sx={{
                          fontWeight: 800,
                          color: caseManagerPalette.ink,
                          bgcolor: caseManagerPalette.heroBlueCloud,
                          lineHeight: '34px',
                          fontFamily: publicBodyFontFamily,
                        }}
                      >
                        {group.label}
                      </ListSubheader>,
                      ...group.options.map((option) => (
                        <MenuItem key={option} value={option} sx={{ pl: 3, fontFamily: publicBodyFontFamily }}>
                          {option}
                        </MenuItem>
                      ))
                    ])}

                    <Divider />
                    <MenuItem value="custom" sx={{ fontWeight: 700, color: caseManagerPalette.heroBlueMid, fontFamily: publicBodyFontFamily }}>
                      Custom accent or speech pattern
                    </MenuItem>
                  </Select>
                </FormControl>

                {data.characterAccent === 'custom' && (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Custom Accent Description"
                    value={data.characterAccentCustom || ''}
                    onChange={(e) => handleAccentCustomChange(e.target.value)}
                    placeholder="Describe how the patient sounds, phrases things, or speaks."
                    sx={publicTextFieldSx}
                  />
                )}

                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  label="Personality Notes"
                  placeholder="For example: anxious but cooperative, answers briefly unless prompted, worried about cancer, pauses when discussing symptoms."
                  value={resolvePersonalityText()}
                  onChange={(e) => handlePersonalityTextChange(e.target.value)}
                  helperText="Use this to shape tone, confidence, emotional cues, and how guarded or forthcoming the patient is."
                  sx={publicTextFieldSx}
                />
              </Stack>
            </Paper>

            <Paper sx={{ ...caseManagerInsetSurfaceSx, p: { xs: 1.6, md: 1.9 } }}>
              <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.4 }}>
                <EditNoteIcon sx={{ color: caseManagerPalette.heroBlueMid }} />
                <Typography sx={{ color: caseManagerPalette.ink, fontWeight: 800, fontFamily: publicBodyFontFamily }}>
                  Quick checklist
                </Typography>
              </Stack>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText primary="The narrative should sound like a patient, not a mark scheme." primaryTypographyProps={{ fontFamily: publicBodyFontFamily, color: caseManagerPalette.ink }} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="Keep the patient profile, narrative, and chosen character consistent with each other." primaryTypographyProps={{ fontFamily: publicBodyFontFamily, color: caseManagerPalette.ink }} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="Use personality notes to guide pace, confidence, and emotional tone." primaryTypographyProps={{ fontFamily: publicBodyFontFamily, color: caseManagerPalette.ink }} />
                </ListItem>
              </List>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </CaseEditorStepFrame>
  );
};

export default PatientNarrativeComposer;
